import { FeatureName, useAuth } from "@accurx/auth";
import { isArchivedWorkspace } from "@accurx/workspace-management";
import { Switch, useLocation } from "react-router-dom";
import { useSelectedOrganisation } from "reduxQuarantine/SelectedOrganisationProvider";
import styled from "styled-components";

import { SimpleRoute } from "app/AppRoute";
import { ComposeRoutesWithFullScreenPage } from "app/ComposeRoutesWithFullScreenPage/ComposeRoutesWithFullScreenPage";
import { FeatureGuard } from "app/FeatureGuard";
import { PatientProfileRedirect } from "app/PatientProfileRedirect";
import { ClinicianConversation } from "app/clinicianconversation/ClinicianConversation";
import { ClinicianConversationProvider } from "app/clinicianconversation/providers";
import { ChooseOrgPage } from "app/customQuestionnaireImport/chooseOrg/ChooseOrgPage";
import { GuardedRoute } from "app/guardedRoutes/GuardedRoute";
import {
    ACCOUNT_SETTINGS_ROUTES,
    AccountSettingsRoutesSection,
} from "app/guardedRoutes/routes/AccountSettingsRoutesSection";
import { BatchMessagingRoutesSection } from "app/guardedRoutes/routes/BatchMessagingRoutesSection";
import {
    RECORD_VIEW_ROUTES,
    RecordViewRoutesSection,
} from "app/guardedRoutes/routes/RecordViewRoutesSection";
import MessagePractice from "app/messagePractice/MessagePracticeContainer";
import { AllPatientLists } from "app/patientLists/AllPatientLists";
import { PatientListPage } from "app/patientLists/PatientListPage";
import { EXAMPLE_REQUEST_ID } from "app/recordView/RecordView.constant";
import SendMessage from "app/sendMessage/SendMessageContainer";
import VideoLobby from "app/videoLobby/VideoLobbyContainer";
import { WorkspaceRoutes } from "app/workspace/WorkspaceRoutes";

import {
    PATIENT_LISTS_ID_ROUTE,
    ROUTES,
    ROUTES_BASE,
    ROUTES_EXTENSION,
    ROUTES_PRIMARY,
} from "../../../shared/Routes";

export const INTERNAL_ROUTES = [
    "/compose",
    "/w/:workspaceId/compose",
    ROUTES_PRIMARY.workspaceBase,
    ROUTES_PRIMARY.batchMessages,
    ROUTES.importChooseOrg,
    ROUTES.reply_from_web,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES.reply_from_web}`,
    ROUTES.message_gp,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES.message_gp}`,
    ROUTES.message_gp_test,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES.message_gp_test}`,
    ...ACCOUNT_SETTINGS_ROUTES,
    ROUTES.video_confirmation,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.videoConfirmation}`,
    ROUTES.send_message,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.sendMessage}`,
    ...RECORD_VIEW_ROUTES,
    ROUTES.message_practice,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.messagePractice}`,
    ROUTES.patient_lists,
    PATIENT_LISTS_ID_ROUTE,
    ROUTES_BASE.patientsListsAll,
    `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.patientProfile}`,
];

export const InternalRoutes = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const { user } = useAuth();
    const { selectedOrgId: workspaceId } = useSelectedOrganisation();

    const currentWorkspace =
        user.isLoggedIn && workspaceId
            ? user.organisations.find(({ orgId }) => orgId === workspaceId)
            : null;

    const isArchived = currentWorkspace
        ? isArchivedWorkspace(currentWorkspace)
        : false;

    const isDemoPatient = params.get("request") === EXAMPLE_REQUEST_ID;

    return (
        <Switch>
            {!isArchived && (
                <GuardedRoute
                    requires={{
                        authenticationStatus: "LoggedInWith2FA",
                        onboarded: true,
                    }}
                    path={["/compose", "/w/:workspaceId/compose"]}
                >
                    <ComposeRoutesWithFullScreenPage />
                </GuardedRoute>
            )}

            <SimpleRoute path={ROUTES_PRIMARY.workspaceBase}>
                <WorkspaceRoutes />
            </SimpleRoute>

            <SimpleRoute path={ROUTES_PRIMARY.batchMessages}>
                <BatchMessagingRoutesSection />
            </SimpleRoute>

            <GuardedRoute
                requires={{
                    onboarded: true,
                    authenticationStatus: "LoggedIn",
                }}
                path={ROUTES.importChooseOrg}
                isOrganisationRoute // Note: Can't switch to SimpleRoute without handling specific redirect logic
            >
                <ChooseOrgPage />
            </GuardedRoute>

            <StyledContainer>
                <Switch>
                    <SimpleRoute
                        path={[
                            ROUTES.reply_from_web,
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES.reply_from_web}`,
                        ]}
                    >
                        <ClinicianConversationProvider.FromUrlId>
                            <ClinicianConversation />
                        </ClinicianConversationProvider.FromUrlId>
                    </SimpleRoute>
                    <SimpleRoute
                        path={[
                            ROUTES.message_gp,
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES.message_gp}`,
                        ]}
                    >
                        <ClinicianConversationProvider.FromNewConversation>
                            <ClinicianConversation />
                        </ClinicianConversationProvider.FromNewConversation>
                    </SimpleRoute>
                    <SimpleRoute
                        path={[
                            ROUTES.message_gp_test,
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES.message_gp_test}`,
                        ]}
                    >
                        <ClinicianConversationProvider.FromTestConversation>
                            <ClinicianConversation />
                        </ClinicianConversationProvider.FromTestConversation>
                    </SimpleRoute>
                    <SimpleRoute path={ACCOUNT_SETTINGS_ROUTES}>
                        <AccountSettingsRoutesSection />
                    </SimpleRoute>
                    <SimpleRoute
                        path={[
                            ROUTES.video_confirmation,
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.videoConfirmation}`,
                        ]}
                        exact
                    >
                        <FeatureGuard all={[FeatureName.VideoConsultWeb]}>
                            <VideoLobby />
                        </FeatureGuard>
                    </SimpleRoute>

                    <SimpleRoute
                        path={[
                            ROUTES.send_message,
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.sendMessage}`,
                        ]}
                        exact
                    >
                        <FeatureGuard all={[FeatureName.PatientThreadSMS]}>
                            <SendMessage user={user} />
                        </FeatureGuard>
                    </SimpleRoute>
                    <GuardedRoute
                        path={RECORD_VIEW_ROUTES}
                        requires={{
                            authenticationStatus: isDemoPatient
                                ? "LoggedIn"
                                : "LoggedInWith2FA",
                            onboarded: true,
                        }}
                    >
                        <RecordViewRoutesSection />
                    </GuardedRoute>

                    <SimpleRoute
                        path={[
                            ROUTES.message_practice,
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.messagePractice}`,
                        ]}
                        exact
                    >
                        <FeatureGuard all={[FeatureName.CaregiverInitiated]}>
                            <MessagePractice user={user} />
                        </FeatureGuard>
                    </SimpleRoute>
                    <GuardedRoute
                        requires={{
                            onboarded: true,
                            authenticationStatus: "LoggedInWith2FA",
                        }}
                        path={[ROUTES.patient_lists, PATIENT_LISTS_ID_ROUTE]}
                        exact
                    >
                        <PatientListPage />
                    </GuardedRoute>
                    <SimpleRoute path={ROUTES_BASE.patientsListsAll} exact>
                        <AllPatientLists />
                    </SimpleRoute>
                    <SimpleRoute
                        path={[
                            `${PATIENT_LISTS_ID_ROUTE}${ROUTES_EXTENSION.patientProfile}`,
                        ]}
                    >
                        <PatientProfileRedirect />
                    </SimpleRoute>
                </Switch>
            </StyledContainer>
        </Switch>
    );
};

export const StyledContainer = styled.div`
    margin: 0rem 2rem 1rem;
`;
